import { useCallback, useMemo } from 'react';
import _ from 'lodash';

import { SERVER_ERRORS } from '@/common/constants';
import routes from '@/common/routes';
import {
  GlobalLoaderMessages,
  useGlobalLoaderStore,
} from '@/components/elements/FullLoader/store/useGlobalLoader.store';
import type { IOrganizationInfoDto } from '@/dtos/models/IOrganizationInfoDto';
import useTOCSidebar from '@/modules/core/components/TOCSidebar/TOCSidebar.store';
import { SnackbarWithRedirect } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import useGetEmployer from '@/modules/employer/hooks/useGetEmployer.query';
import { useSetupEmployerStore } from '@/modules/employer/store';

import { LogoType } from '../../Branding/branding.types';
import { useUploadOrganizationLogos } from '../../Branding/query/useUploadOrganizationLogos';
import { useStore } from '../../Branding/store/useOrganizationSetupLogo.store';
import { useSaveEmployer, useServerErrors, useSetupEmployerMode } from '../../hooks';
import { HARD_TO_REPRODUCE_ERROR_MESSAGE } from '../../hooks/useSaveEmployer';
import { useModalType, useSaveTypeStore } from '../../stores';
import { OVERRIDABLE_PROPERTIES_CONFIG } from '../../SyncUpdatesModal/SyncUpdatesModal.constants';
import type { PropertyChangeItem } from '../../SyncUpdatesModal/SyncUpdatesModal.types';
import { normalizePropertyName } from '../../SyncUpdatesModal/utils/normalizePropertyName';

import useSaveValidate from './useSaveValidate';

const buildSyncModalChanges = (response: IOrganizationInfoDto): PropertyChangeItem[] =>
  (response.modified_overridable_properties ?? []).map((item) => {
    const propertyName = normalizePropertyName(item.property_name ?? '');
    const config = OVERRIDABLE_PROPERTIES_CONFIG[propertyName];
    return {
      propertyName,
      displayName: item.display_name ?? '',
      oldValue: item.old_value ?? null,
      newValue: item.new_value ?? null,
      groupType: item.group_type ?? (config?.groupType as string) ?? '',
      section: config?.section ?? '',
      description: config?.description,
      hasDependencyWarning: config?.hasDependencyWarning ?? false,
      isRoot: config?.isRoot ?? false,
    };
  });

const LOGO_SECURITY_ERROR = 400;
let timerId: ReturnType<typeof setTimeout>;

export default () => {
  const {
    setActivateModal,
    setPendingModal,
    setActivateSaveModal,
    setIsEmptySaveModal,
    setIsActivatingModal,
    setSuccessfullyActivatedModal,
    setSuccessfullySavedModal,
    setUnableToActivateModal,
    setErrorTextModal,
    setSyncModal,
    setSyncModalChanges,
  } = useModalType();
  const {
    redirectToHome, mode, toView,
  } = useSetupEmployerMode();
  const { employerID } = useSetupEmployerMode();
  const { formattedData, refetch } = useGetEmployer();
  const status = formattedData.organizationStatusType;
  const { handleSave, handleActivateEmployerAsync } = useSaveEmployer();
  const {
    formatedPendingErrors,
    formatedPendingSuccesses,
    formatedActivateErrors,
  } = useSaveValidate();
  const {
    setIsUnableToActivate,
  } = useTOCSidebar();
  const {
    handleSetPendingStatusType, handleSetActiveStatusType,
    pendingSubmitted,
    activateSubmitted,
  } = useSaveTypeStore();
  const { setLoader, removeLoading } = useGlobalLoaderStore();
  const { handleAddPermanentSnackbar, handleReset: handleResetSnackbar } = useSnackbar();
  const { handleServerErrors, handleResetErrors } = useServerErrors();

  const largeDarkLogo = useStore((store) => store.largeDarkLogo);
  const largeLightLogo = useStore((store) => store.largeLightLogo);
  const miniDarkLogo = useStore((store) => store.miniDarkLogo);
  const miniLightLogo = useStore((store) => store.miniLightLogo);
  const securityError = useStore((store) => store.securityError);
  const setSecurityError = useStore((store) => store.setSecurityError);
  const handleResetLogosErrors = useStore((store) => store.handleResetErrors);
  // @ts-ignore
  const setLogoSecurityError = useCallback((error, logoType: LogoType) => {
    if (error?.response?.status !== LOGO_SECURITY_ERROR) return;

    if (logoType === LogoType.largeDark) {
      setSecurityError({
        ...securityError,
        largeDarkLogo: true,
      });
    } else if (logoType === LogoType.largeLight) {
      setSecurityError({
        ...securityError,
        largeLightLogo: true,
      });
    } else if (logoType === LogoType.minimizedDark) {
      setSecurityError({
        ...securityError,
        miniDarkLogo: true,
      });
    } else if (logoType === LogoType.minimizedLight) {
      setSecurityError({
        ...securityError,
        miniLightLogo: true,
      });
    }
  }, [securityError, setSecurityError]);

  const {
    uploadLogo: uploadLargeDarkLogo,
  } = useUploadOrganizationLogos(formattedData.id, LogoType.largeDark, setLogoSecurityError);
  const {
    uploadLogo: uploadLargeLightLogo,
  } = useUploadOrganizationLogos(formattedData.id, LogoType.largeLight, setLogoSecurityError);
  const {
    uploadLogo: uploadMiniDarkLogo,
  } = useUploadOrganizationLogos(formattedData.id, LogoType.minimizedDark, setLogoSecurityError);
  const {
    uploadLogo: uploadMiniLightLogo,
  } = useUploadOrganizationLogos(formattedData.id, LogoType.minimizedLight, setLogoSecurityError);

  const { employer } = useSetupEmployerStore();
  const employerName = useMemo(() => employer?.employerName, [employer]);
  const snackbarRedirectOption: SnackbarWithRedirect['redirect'] = useMemo(() => ({
    path: routes.HOME,
    label: `Exit ${mode} setup`,
  }), [mode]);
  const handleUploadLogos = useCallback(async () => {
    if (largeDarkLogo) {
      await uploadLargeDarkLogo(largeDarkLogo);
    }
    if (largeLightLogo) {
      await uploadLargeLightLogo(largeLightLogo);
    }
    if (miniDarkLogo) {
      await uploadMiniDarkLogo(miniDarkLogo);
    }
    if (miniLightLogo) {
      await uploadMiniLightLogo(miniLightLogo);
    }
  }, [
    uploadLargeDarkLogo,
    uploadLargeLightLogo,
    uploadMiniDarkLogo,
    uploadMiniLightLogo,
    largeDarkLogo,
    largeLightLogo,
    miniDarkLogo,
    miniLightLogo,
  ]);

  const handleSavePending = useCallback(async () => {
    handleResetErrors();
    await handleSetPendingStatusType(true);
    if (_.isEmpty(formatedPendingErrors)) {
      try {
        setLoader({
          title: `Saving changes for ${employerName}`,
          message: GlobalLoaderMessages.SAVING_CHANGES,
          contentWidth: '720px',
        });
        await handleUploadLogos();
        await handleSave();
        await removeLoading(2000);
        handleResetLogosErrors();

        setSuccessfullySavedModal(true);
      } catch (e) {
        const code = _.get(e, 'response.status', 0) as number;
        const isHardToReproduceError = _.get(e, 'message') === HARD_TO_REPRODUCE_ERROR_MESSAGE;

        if (isHardToReproduceError) {
          setErrorTextModal(HARD_TO_REPRODUCE_ERROR_MESSAGE);
        } else if (code !== SERVER_ERRORS.BAD_REQUEST) {
          setPendingModal(true);
        } else if (code === SERVER_ERRORS.UNAUTHORIZED || !code) {
          return;
        } else {
          setIsEmptySaveModal(true);
        }
        handleServerErrors(e as object);

        await removeLoading(1000);
      }
    } else if (_.isEmpty(formatedPendingSuccesses)) {
      setIsEmptySaveModal(true);
    } else if (!pendingSubmitted) {
      setPendingModal(true);
    }
  }, [
    setErrorTextModal,
    handleResetErrors,
    handleSetPendingStatusType,
    formatedPendingErrors,
    formatedPendingSuccesses,
    pendingSubmitted,
    setLoader,
    employerName,
    handleSave,
    removeLoading,
    handleServerErrors,
    setPendingModal,
    setIsEmptySaveModal,
    handleResetLogosErrors,
    handleUploadLogos,
    setSuccessfullySavedModal,
  ]);
  // const activatingMessage = useMemo(() => {
  //   switch (mode) {
  //     case EmployerSetupModes.employer: {
  //       return GlobalLoaderMessages.EMPLOYER_ACTIVATING;
  //     }
  //     case EmployerSetupModes.partner: {
  //       return GlobalLoaderMessages.PARTNER_ACTIVATING;
  //     }
  //     case EmployerSetupModes.distributor: {
  //       return GlobalLoaderMessages.DISTRIBUTOR_ACTIVATING;
  //     }
  //     default: {
  //       return GlobalLoaderMessages.PARTNER_ACTIVATING;
  //     }
  //   }
  // }, [mode]);

  const handleActivate = useCallback(async () => {
    await handleSetActiveStatusType(true);
    if (_.isEmpty(formatedActivateErrors) && employerID) {
      let isActivatingRunning = false;
      const activatingIsRunning = () => {
        setIsActivatingModal(false);
        setUnableToActivateModal(true);
        setIsUnableToActivate(true);
        handleAddPermanentSnackbar({
          text: `${employerName} is activating…`,
          closeIcon: true,
          redirect: snackbarRedirectOption,
        });
      };
      try {
        if (status !== EmployerStatus.Active && !formattedData.isActivationStarted) {
          setIsActivatingModal(true);

          const activateAsync = async () => {
            await handleUploadLogos();
            await handleSave();
            await handleActivateEmployerAsync(employerID);
            setTimeout(() => refetch(), 2000);
            handleResetLogosErrors();
            setIsUnableToActivate(false);
            handleAddPermanentSnackbar({
              text: `${employerName} is activated!`,
              closeIcon: true,
              redirect: snackbarRedirectOption,
            });
          };
          const successfullyActivated = () => {
            if (isActivatingRunning) return;
            setIsActivatingModal(false);
            setSuccessfullyActivatedModal(true);
          };

          timerId = setTimeout(() => {
            isActivatingRunning = true;
            activatingIsRunning();
          }, 5000);
          await activateAsync();
          clearTimeout(timerId);
          successfullyActivated();
        } else {
          setLoader({
            title: `Saving changes for ${employerName}`,
            message: GlobalLoaderMessages.SAVING_CHANGES,
            contentWidth: '720px',
          });
          await handleUploadLogos();
          const response = await handleSave();
          await removeLoading(2000);
          handleResetLogosErrors();

          const shouldShowSyncModal =
            response?.has_child_organizations &&
            response.modified_overridable_properties?.length;

          if (shouldShowSyncModal) {
            setSyncModalChanges(buildSyncModalChanges(response));
            setSyncModal(true);
          } else {
            toView();
            setSuccessfullySavedModal(true);
          }
        }
      } catch (e) {
        if (timerId) clearTimeout(timerId);
        if (isActivatingRunning) return;
        handleResetSnackbar();
        setUnableToActivateModal(false);
        setIsUnableToActivate(false);
        setIsActivatingModal(false);

        const code = _.get(e, 'response.status', 0) as number;
        const isHardToReproduceError = _.get(e, 'message') === HARD_TO_REPRODUCE_ERROR_MESSAGE;

        if (isHardToReproduceError) {
          setErrorTextModal(HARD_TO_REPRODUCE_ERROR_MESSAGE);
        } else if (code !== SERVER_ERRORS.BAD_REQUEST) {
          setActivateSaveModal(true);
        } else if (code === SERVER_ERRORS.UNAUTHORIZED || !code) {
          return;
        } else {
          setActivateModal(true);
        }

        handleServerErrors(e as object);
        await removeLoading(2000);
      }
    } else if (!activateSubmitted) {
      setActivateModal(true);
    }
  }, [
    setErrorTextModal,
    handleResetSnackbar,
    formattedData.isActivationStarted,
    handleSetActiveStatusType,
    formatedActivateErrors,
    employerID,
    status,
    setLoader,
    handleSave,
    handleActivateEmployerAsync,
    removeLoading,
    snackbarRedirectOption,
    handleAddPermanentSnackbar,
    employerName,
    handleServerErrors,
    setActivateSaveModal,
    setActivateModal,
    activateSubmitted,
    refetch,
    handleResetLogosErrors,
    setIsActivatingModal,
    setSuccessfullyActivatedModal,
    setSuccessfullySavedModal,
    setUnableToActivateModal,
    setIsUnableToActivate,
    handleUploadLogos,
    setSyncModal,
    setSyncModalChanges,
    toView,
  ]);

  const handleDiscard = () => {
    redirectToHome();
  };

  return {
    handleSavePending,
    handleActivate,
    handleDiscard,
  };
};
